import { supabase } from '../lib/supabase';
import { safeStorageName } from '../utils/safeStorageName';
import { normalize as normalizeNomenclature } from '../utils/directiveNomenclature';
import { projectExtrasService } from './projectExtrasService';

/**
 * Directives — le registre des documents recus du professionnel.
 *
 * Une directive est une ORIGINE : elle dit d'ou vient un extra ou un credit,
 * jamais combien il vaut. Aucun montant ne transite par ce service.
 *
 * Deux dates coexistent en base et ne veulent pas dire la meme chose :
 *   date_directive  la date DU DOCUMENT — celle de la liste, du tri et des exports
 *   created_at      la date de SAISIE — affichee SEULEMENT dans la fiche, sous
 *                   le libelle « Saisie le », a cote de « Saisie par »
 * La liste, le tri et les exports n'utilisent jamais created_at.
 */

const BUCKET = 'contracts-documents';

// Meme convention que les autres entites : {type}/{id}, dans contracts-documents.
export const directiveStoragePath = (directiveId) => `directives/${directiveId}`;

// Les deux tables se referencent mutuellement (directive.extra_id et
// extra.directive_id) : PostgREST exige le nom de contrainte explicite.
const DIRECTIVE_SELECT = `
  id, project_id, number, nomenclature, division, professional_id,
  title, description, date_directive, rfi_id, outcome, extra_id,
  outcome_at, outcome_by, created_at, created_by,
  professional:professionals!project_directives_professional_id_fkey ( id, company_name ),
  rfi:project_rfis!project_directives_rfi_id_fkey ( id, rfi_number, subject, status, question ),
  extra:project_extras!project_directives_extra_id_fkey ( id, co_number, type, title )
`;

const currentUserId = async () => {
  try {
    const { data } = await supabase?.auth?.getUser();
    return data?.user?.id || null;
  } catch {
    return null;
  }
};

/** Toutes les directives d'un projet, dans l'ordre de leur numero. */
export const listDirectives = async (projectId) => {
  if (!projectId) return [];
  try {
    const { data, error } = await supabase
      ?.from('project_directives')
      ?.select(DIRECTIVE_SELECT)
      ?.eq('project_id', projectId)
      ?.order('number', { ascending: true });
    if (error) {
      console.error('[directives] listDirectives:', error?.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('[directives] listDirectives:', err);
    return [];
  }
};

/** Professionnels actifs rattaches au projet, pour le selecteur de la modale. */
export const listProjectProfessionals = async (projectId) => {
  if (!projectId) return [];
  try {
    const { data, error } = await supabase
      ?.from('project_professionals')
      ?.select('professional_id, role, active, professional:professionals ( id, company_name, profession )')
      ?.eq('project_id', projectId)
      ?.eq('active', true);
    if (error) {
      console.error('[directives] listProjectProfessionals:', error?.message);
      return [];
    }
    return (data || [])
      ?.map((row) => row?.professional)
      ?.filter((p) => p?.id)
      ?.sort((a, b) => (a?.company_name || '')?.localeCompare(b?.company_name || ''));
  } catch (err) {
    console.error('[directives] listProjectProfessionals:', err);
    return [];
  }
};

/** QRT du projet — le lien est facultatif, aucune validation croisee. */
export const listProjectRfis = async (projectId) => {
  if (!projectId) return [];
  try {
    const { data, error } = await supabase
      ?.from('project_rfis')
      ?.select('id, rfi_number, subject')
      ?.eq('project_id', projectId)
      ?.order('rfi_number', { ascending: true });
    if (error) {
      console.error('[directives] listProjectRfis:', error?.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('[directives] listProjectRfis:', err);
    return [];
  }
};

const UNIQUE_VIOLATION = '23505';
// Index unique de la nomenclature normalisee, par projet (pose en base).
const NOMENCLATURE_INDEX = 'project_directives_nomenclature_norm_uidx';

// Doublon de nomenclature refuse par l'index : erreur lisible, jamais de reessai.
const isNomenclatureConflict = (error) =>
  error?.code === UNIQUE_VIOLATION && String(error?.message || '')?.includes(NOMENCLATURE_INDEX);

const nomenclatureTakenError = () => {
  const err = new Error('NOMENCLATURE_TAKEN');
  err.code = 'NOMENCLATURE_TAKEN';
  return err;
};

/**
 * Vrai si une autre directive du projet porte deja cette nomenclature, une fois
 * normalisee comme l'index (majuscules, sans espaces, tirets ni underscores).
 * Une lecture des nomenclatures du projet, comparaison cote app : la meme
 * fonction normalize() que l'index, sans dependre d'un filtre SQL.
 */
export const isNomenclatureTaken = async (projectId, nomenclature, excludeId = null) => {
  const key = normalizeNomenclature(nomenclature);
  if (!projectId || !key) return false;
  const { data, error } = await supabase
    ?.from('project_directives')
    ?.select('id, nomenclature')
    ?.eq('project_id', projectId)
    ?.not('nomenclature', 'is', null);
  if (error) {
    console.error('[directives] isNomenclatureTaken:', error?.message);
    throw error;
  }
  return (data || [])?.some((row) => row?.id !== excludeId && normalizeNomenclature(row?.nomenclature) === key);
};

/**
 * Cree une directive.
 *
 * `number` n'est JAMAIS envoye : un trigger BEFORE INSERT le pose. Deux
 * creations simultanees peuvent donc viser le meme numero ; dans ce cas
 * l'index unique rejette la seconde et un simple reessai suffit, le trigger
 * relisant le maximum a jour. Un seul reessai : au-dela, c'est autre chose.
 */
export const createDirective = async ({
  projectId,
  nomenclature,
  division,
  professionalId,
  title,
  description,
  dateDirective,
  rfiId,
}) => {
  const payload = {
    project_id: projectId,
    nomenclature: nomenclature?.trim() || null,
    division: division || null,
    professional_id: professionalId || null,
    title: title?.trim() || null,
    description: description?.trim() || null,
    date_directive: dateDirective || null,
    rfi_id: rfiId || null,
    outcome: 'pending',
    created_by: await currentUserId(),
  };

  const attempt = async () =>
    supabase?.from('project_directives')?.insert(payload)?.select(DIRECTIVE_SELECT)?.single();

  let { data, error } = await attempt();
  // Reessai seulement pour le numero (trigger) ; un doublon de nomenclature ne
  // se regle pas en reessayant.
  if (error?.code === UNIQUE_VIOLATION && !isNomenclatureConflict(error)) {
    ({ data, error } = await attempt());
  }
  if (isNomenclatureConflict(error)) throw nomenclatureTakenError();
  if (error) {
    console.error('[directives] createDirective:', error?.message);
    throw error;
  }
  return data;
};

// ─── Pieces jointes : table directive_documents ──────────────────────────────
// file_path : cle Storage (safeStorageName + prefixe horodate), ne bouge jamais.
// file_name : nom d'origine tel que televerse, renommable.
const DOCS_TABLE = 'directive_documents';
const DOC_SELECT = 'id, directive_id, project_id, file_name, file_path, mime_type, size_bytes, created_at';

/**
 * Nombre de pieces par directive pour tout le projet, en UNE requete.
 * Renvoie { [directiveId]: n } (une directive absente = 0), ou null si la
 * lecture echoue : l'appelant retombe alors sur countDirectiveFiles.
 */
export const listDirectiveDocumentCounts = async (projectId) => {
  if (!projectId) return {};
  try {
    const { data, error } = await supabase
      ?.from(DOCS_TABLE)
      ?.select('directive_id')
      ?.eq('project_id', projectId);
    if (error) {
      console.error('[directives] listDirectiveDocumentCounts:', error?.message);
      return null;
    }
    return (data || [])?.reduce((acc, row) => {
      acc[row?.directive_id] = (acc?.[row?.directive_id] || 0) + 1;
      return acc;
    }, {});
  } catch (err) {
    console.error('[directives] listDirectiveDocumentCounts:', err);
    return null;
  }
};

/** Pieces d'une directive, dans l'ordre d'ajout. */
export const listDirectiveDocuments = async (directiveId) => {
  if (!directiveId) return [];
  const { data, error } = await supabase
    ?.from(DOCS_TABLE)
    ?.select(DOC_SELECT)
    ?.eq('directive_id', directiveId)
    ?.order('created_at', { ascending: true });
  if (error) {
    console.error('[directives] listDirectiveDocuments:', error?.message);
    throw error;
  }
  return data || [];
};

/**
 * Televerse une piece jointe dans directives/{id}/ puis l'enregistre dans
 * directive_documents. Si l'enregistrement echoue, l'objet Storage est retire :
 * jamais de fichier orphelin. Ne cree aucun bucket.
 */
export const uploadDirectiveFile = async (directiveId, file, projectId) => {
  if (!directiveId || !file) return null;
  const path = `${directiveStoragePath(directiveId)}/${Date.now()}_${safeStorageName(file?.name)}`;
  const { error } = await supabase?.storage?.from(BUCKET)?.upload(path, file, { upsert: false });
  if (error) {
    console.error('[directives] uploadDirectiveFile:', error?.message);
    throw error;
  }
  const { data: row, error: insertError } = await supabase
    ?.from(DOCS_TABLE)
    ?.insert({
      directive_id: directiveId,
      project_id: projectId,
      file_path: path,
      file_name: file?.name || safeStorageName(file?.name),
      mime_type: file?.type || null,
      size_bytes: file?.size ?? null,
      created_by: await currentUserId(),
    })
    ?.select(DOC_SELECT)
    ?.single();
  if (insertError) {
    console.error('[directives] uploadDirectiveFile (enregistrement):', insertError?.message);
    await supabase?.storage?.from(BUCKET)?.remove([path]);
    throw insertError;
  }
  return row;
};

/**
 * Supprime une piece : la ligne d'abord (la piece disparait de la liste), puis
 * l'objet Storage. Un objet qui resterait n'est plus visible nulle part ; une
 * ligne sans objet, elle, afficherait un lien mort.
 */
export const deleteDirectiveFile = async (doc) => {
  if (!doc?.id) return;
  const { error } = await supabase?.from(DOCS_TABLE)?.delete()?.eq('id', doc?.id);
  if (error) {
    console.error('[directives] deleteDirectiveFile:', error?.message);
    throw error;
  }
  if (doc?.file_path) {
    const { error: storageError } = await supabase?.storage?.from(BUCKET)?.remove([doc?.file_path]);
    if (storageError) console.error('[directives] deleteDirectiveFile (Storage):', storageError?.message);
  }
};

// Extension d'un nom, point compris (« .pdf »), ou '' s'il n'y en a pas.
const extensionOf = (name) => {
  const match = String(name || '')?.match(/\.[A-Za-z0-9]{1,8}$/);
  return match ? match?.[0] : '';
};

/**
 * Renomme une piece : seul file_name change, l'objet Storage ne bouge pas.
 * Nom vide refuse ; si l'utilisateur retire l'extension, elle est remise.
 */
export const renameDirectiveDocument = async (id, newName, currentName) => {
  let name = String(newName || '')?.trim();
  if (!id || !name) throw new Error('EMPTY_NAME');
  const ext = extensionOf(currentName);
  if (ext && !name?.toLowerCase()?.endsWith(ext?.toLowerCase())) name = `${name}${ext}`;
  const { data, error } = await supabase
    ?.from(DOCS_TABLE)
    ?.update({ file_name: name })
    ?.eq('id', id)
    ?.select(DOC_SELECT)
    ?.single();
  if (error) {
    console.error('[directives] renameDirectiveDocument:', error?.message);
    throw error;
  }
  return data;
};

/** URL signee d'une piece ; `download` force le telechargement sous son nom affiche. */
export const getDirectiveDocumentUrl = async (doc, download = false) => {
  if (!doc?.file_path) return null;
  const { data, error } = await supabase?.storage
    ?.from(BUCKET)
    ?.createSignedUrl(doc?.file_path, 60 * 60, download ? { download: doc?.file_name || true } : undefined);
  if (error) throw error;
  return data?.signedUrl || null;
};

/**
 * Nom standard propose pour une piece :
 *   <ProjetNo>_<Nomenclature>_<Titre court>_<AAAA-MM-JJ>.pdf
 * Titre tronque a 40 caracteres ; chaque partie passe par safeStorageName
 * (lisible, sans accents ni caracteres speciaux). Date : reception de la
 * directive, sinon aujourd'hui.
 */
export const suggestStandardName = (project, directive) => {
  const today = new Date();
  const pad = (n) => String(n)?.padStart(2, '0');
  const date = directive?.date_directive
    ? String(directive?.date_directive)?.slice(0, 10)
    : `${today?.getFullYear()}-${pad(today?.getMonth() + 1)}-${pad(today?.getDate())}`;
  const part = (value, max) => {
    const raw = String(value || '')?.trim();
    if (!raw) return '';
    const clean = safeStorageName(max ? raw?.slice(0, max) : raw);
    return clean === 'document' ? '' : clean?.replace(/_+$/, '');
  };
  const parts = [
    part(project?.projectNumber ?? project?.project_number),
    part(directive?.nomenclature),
    part(directive?.title, 40),
    date,
  ]?.filter(Boolean);
  return `${parts?.join('_')}.pdf`;
};

// Au-dela, le compte est abandonne : la pastille affiche « ? » au lieu d'attendre.
const COUNT_TIMEOUT_MS = 8000;

/**
 * Nombre de pieces jointes d'une directive, en un aller-retour.
 * null = inconnu (delai de 8 s depasse ou erreur) ; jamais de promesse bloquee.
 */
export const countDirectiveFiles = async (directiveId) => {
  if (!directiveId) return 0;
  let timer;
  try {
    const listing = supabase?.storage
      ?.from(BUCKET)
      ?.list(directiveStoragePath(directiveId), { limit: 100 });
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve({ timedOut: true }), COUNT_TIMEOUT_MS);
    });
    const result = await Promise.race([listing, timeout]);
    if (result?.timedOut || result?.error) return null;
    return (result?.data || [])?.filter((f) => f?.name && f?.name !== '.emptyFolderPlaceholder')?.length;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Classe la directive sans impact. extra_id reste NULL — la contrainte de la
 * base l'exige pour pending et no_impact.
 */
export const markNoImpact = async (directiveId) => {
  const { data, error } = await supabase
    ?.from('project_directives')
    ?.update({
      outcome: 'no_impact',
      extra_id: null,
      outcome_at: new Date()?.toISOString(),
      outcome_by: await currentUserId(),
    })
    ?.eq('id', directiveId)
    ?.select(DIRECTIVE_SELECT)
    ?.single();
  if (error) {
    console.error('[directives] markNoImpact:', error?.message);
    throw error;
  }
  return data;
};

/**
 * Genere l'extra ou le credit issu de la directive, puis relie les deux.
 *
 * L'extra ne porte AUCUN montant : cost_submitted et cost_accepted restent
 * vides. `amount` est mis a 0 parce que la colonne est NOT NULL depuis la
 * creation de la table (contrainte actuelle : amount >= 0) — c'est une
 * exigence de la base, pas un prix.
 *
 * Si l'extra part mais que la directive refuse la mise a jour, l'extra est
 * supprime : un extra issu d'une directive ne doit jamais rester orphelin.
 * Si meme cette suppression echoue, on le dit — plutot qu'un silence.
 */
export const createOutcomeExtra = async ({ directive, type, projectId }) => {
  if (type !== 'extra' && type !== 'credit') {
    throw new Error(`createOutcomeExtra: type invalide "${type}"`);
  }

  const { data: extra, error: extraError } = await supabase
    ?.from('project_extras')
    ?.insert({
      project_id: projectId,
      type,
      title: directive?.title || null,
      description: directive?.description || null,
      division: directive?.division || null,
      directive_id: directive?.id,
      status: 'draft',
      amount: 0,
    })
    ?.select('id, co_number, type, title')
    ?.single();

  if (extraError) {
    console.error('[directives] createOutcomeExtra insert:', extraError?.message);
    throw extraError;
  }

  const { data: updated, error: linkError } = await supabase
    ?.from('project_directives')
    ?.update({
      outcome: type,
      extra_id: extra?.id,
      outcome_at: new Date()?.toISOString(),
      outcome_by: await currentUserId(),
    })
    ?.eq('id', directive?.id)
    ?.select(DIRECTIVE_SELECT)
    ?.single();

  if (linkError) {
    console.error('[directives] createOutcomeExtra link:', linkError?.message);
    const { error: rollbackError } = await supabase
      ?.from('project_extras')
      ?.delete()
      ?.eq('id', extra?.id);
    if (rollbackError) {
      console.error('[directives] rollback impossible:', rollbackError?.message);
      const orphan = new Error('DIRECTIVE_LINK_FAILED_EXTRA_KEPT');
      orphan.extra = extra;
      throw orphan;
    }
    const rolledBack = new Error('DIRECTIVE_LINK_FAILED_EXTRA_ROLLED_BACK');
    throw rolledBack;
  }

  return { directive: updated, extra };
};

// ─── Modifier, changer l'issue, supprimer, historique (lot D2) ────────────────

/** Modifie les champs d'une directive. Les pieces et l'issue ne passent pas ici. */
export const updateDirective = async (directiveId, fields = {}) => {
  const payload = {
    nomenclature: fields?.nomenclature?.trim() || null,
    title: fields?.title?.trim() || null,
    description: fields?.description?.trim() || null,
    division: fields?.division || null,
    date_directive: fields?.date_directive || null,
    professional_id: fields?.professional_id || null,
    rfi_id: fields?.rfi_id || null,
  };
  const { data, error } = await supabase
    ?.from('project_directives')
    ?.update(payload)
    ?.eq('id', directiveId)
    ?.select(DIRECTIVE_SELECT)
    ?.single();
  if (isNomenclatureConflict(error)) throw nomenclatureTakenError();
  if (error) {
    console.error('[directives] updateDirective:', error?.message);
    throw error;
  }
  return data;
};

// Retire le lien extra -> directive. L'extra est conserve.
const detachExtra = async (extraId, directiveId) => {
  if (!extraId) return null;
  const { error } = await supabase
    ?.from('project_extras')
    ?.update({ directive_id: null })
    ?.eq('id', extraId)
    ?.eq('directive_id', directiveId);
  return error || null;
};

export const DIRECTIVE_OUTCOMES = ['pending', 'no_impact', 'extra', 'credit'];

/**
 * Change l'issue d'une directive, quelle qu'elle soit.
 *
 *   pending / no_impact : extra_id passe a NULL (contrainte outcome_link) et
 *     l'extra lie perd son directive_id. L'extra n'est PAS supprime.
 *   extra / credit : un nouvel extra brouillon est cree et relie
 *     (createOutcomeExtra) ; l'ancien extra lie, s'il y en a un, est ensuite
 *     detache et conserve.
 *
 * Si la directive est enregistree mais que l'ancien extra n'a pas pu etre
 * detache, l'erreur EXTRA_DETACH_FAILED le dit (avec la directive a jour).
 */
export const setDirectiveOutcome = async ({ directive, outcome, projectId }) => {
  if (!DIRECTIVE_OUTCOMES.includes(outcome)) {
    throw new Error(`setDirectiveOutcome: issue invalide "${outcome}"`);
  }
  const previousExtraId = directive?.extra_id || null;

  let updated;
  if (outcome === 'extra' || outcome === 'credit') {
    const result = await createOutcomeExtra({ directive, type: outcome, projectId });
    updated = result?.directive;
  } else {
    const resolved = outcome === 'no_impact';
    const { data, error } = await supabase
      ?.from('project_directives')
      ?.update({
        outcome,
        extra_id: null,
        outcome_at: resolved ? new Date()?.toISOString() : null,
        outcome_by: resolved ? await currentUserId() : null,
      })
      ?.eq('id', directive?.id)
      ?.select(DIRECTIVE_SELECT)
      ?.single();
    if (error) {
      console.error('[directives] setDirectiveOutcome:', error?.message);
      throw error;
    }
    updated = data;
  }

  if (previousExtraId) {
    const detachError = await detachExtra(previousExtraId, directive?.id);
    if (detachError) {
      console.error('[directives] setDirectiveOutcome (detachement):', detachError?.message);
      const err = new Error('EXTRA_DETACH_FAILED');
      err.directive = updated;
      throw err;
    }
  }
  return updated;
};

// ─── Relier a un extra existant (lot D5b) ─────────────────────────────────────

/**
 * Extras / credits du projet qu'on peut relier : sans directive_id, ET
 * qu'aucune directive du projet ne pointe deja par extra_id (double filtre :
 * protege des incoherences de l'epoque ou le lien se saisissait a la main).
 */
export const listLinkableExtras = async (projectId) => {
  if (!projectId) return [];
  const [extrasRes, directivesRes] = await Promise.all([
    supabase
      ?.from('project_extras')
      ?.select('id, type, status, co_number, title, amount, cost_submitted, cost_accepted, profit')
      ?.eq('project_id', projectId)
      ?.is('directive_id', null)
      ?.order('created_at', { ascending: false }),
    supabase
      ?.from('project_directives')
      ?.select('extra_id')
      ?.eq('project_id', projectId)
      ?.not('extra_id', 'is', null),
  ]);
  const error = extrasRes?.error || directivesRes?.error;
  if (error) {
    console.error('[directives] listLinkableExtras:', error?.message);
    throw error;
  }
  const taken = new Set((directivesRes?.data || [])?.map((d) => d?.extra_id));
  return (extrasRes?.data || [])?.filter((e) => !taken?.has(e?.id));
};

/**
 * Relie une directive a un extra / credit EXISTANT. Sans RPC, donc en trois
 * temps, chacun reversible :
 *   1. reserver l'extra : directive_id pose SEULEMENT s'il est encore libre ;
 *      0 ligne -> EXTRA_ALREADY_LINKED, rien d'autre n'est ecrit ;
 *   2. UN SEUL UPDATE de la directive (outcome = type de l'extra + extra_id,
 *      ensemble : contrainte outcome_link) ; en cas d'echec, l'extra est
 *      relache puis l'erreur remonte ;
 *   3. si la directive avait deja un autre extra, il est detache (comme en D2) ;
 *      echec -> EXTRA_DETACH_FAILED, la directive etant deja a jour.
 */
export const linkExistingExtra = async ({ directiveId, extraId }) => {
  if (!directiveId || !extraId) throw new Error('linkExistingExtra: directive et extra requis');

  const { data: current, error: readError } = await supabase
    ?.from('project_directives')
    ?.select('extra_id')
    ?.eq('id', directiveId)
    ?.single();
  if (readError) throw readError;
  const previousExtraId = current?.extra_id && current?.extra_id !== extraId ? current?.extra_id : null;

  // 1. Reserver l'extra.
  const { data: claimed, error: claimError } = await supabase
    ?.from('project_extras')
    ?.update({ directive_id: directiveId })
    ?.eq('id', extraId)
    ?.is('directive_id', null)
    ?.select('id, type, co_number');
  if (claimError) {
    console.error('[directives] linkExistingExtra (reservation):', claimError?.message);
    throw claimError;
  }
  const extra = claimed?.[0];
  if (!extra) {
    const err = new Error('EXTRA_ALREADY_LINKED');
    err.code = 'EXTRA_ALREADY_LINKED';
    throw err;
  }
  const outcome = extra?.type === 'credit' ? 'credit' : 'extra';

  // 2. La directive, en un seul UPDATE.
  const { data: updated, error: linkError } = await supabase
    ?.from('project_directives')
    ?.update({
      outcome,
      extra_id: extraId,
      outcome_at: new Date()?.toISOString(),
      outcome_by: await currentUserId(),
    })
    ?.eq('id', directiveId)
    ?.select(DIRECTIVE_SELECT)
    ?.single();
  if (linkError) {
    console.error('[directives] linkExistingExtra (directive):', linkError?.message);
    const { error: releaseError } = await supabase
      ?.from('project_extras')
      ?.update({ directive_id: null })
      ?.eq('id', extraId)
      ?.eq('directive_id', directiveId);
    if (releaseError) console.error('[directives] linkExistingExtra (relache):', releaseError?.message);
    throw linkError;
  }

  // 3. L'ancien extra, s'il y en avait un.
  if (previousExtraId) {
    const detachError = await detachExtra(previousExtraId, directiveId);
    if (detachError) {
      console.error('[directives] linkExistingExtra (detachement):', detachError?.message);
      const err = new Error('EXTRA_DETACH_FAILED');
      err.directive = updated;
      throw err;
    }
  }
  return { directive: updated, extra };
};

/**
 * Supprime une directive. Refusee tant qu'un extra est lie (LINKED_EXTRA) :
 * il faut d'abord changer l'issue. Les lignes directive_documents partent en
 * cascade ; les objets Storage sont retires ensuite (au mieux).
 */
export const deleteDirective = async (directive) => {
  if (!directive?.id) return;
  if (directive?.extra_id) throw new Error('LINKED_EXTRA');

  const { data: docs } = await supabase
    ?.from(DOCS_TABLE)
    ?.select('file_path')
    ?.eq('directive_id', directive?.id);

  const { data, error } = await supabase
    ?.from('project_directives')
    ?.delete()
    ?.eq('id', directive?.id)
    ?.select('id');
  if (error) {
    console.error('[directives] deleteDirective:', error?.message);
    throw error;
  }
  // RLS : une suppression refusee ne renvoie pas d'erreur, juste 0 ligne.
  if (!data?.length) throw new Error('NOT_DELETED');

  const paths = (docs || [])?.map((d) => d?.file_path)?.filter(Boolean);
  if (paths?.length) {
    const { error: storageError } = await supabase?.storage?.from(BUCKET)?.remove(paths);
    if (storageError) console.error('[directives] deleteDirective (Storage):', storageError?.message);
  }
};

// Champs journalises par trg_log_project_directives, dans l'ordre d'affichage.
export const DIRECTIVE_HISTORY_FIELDS = [
  'outcome', 'nomenclature', 'title', 'division', 'date_directive',
  'professional_id', 'rfi_id', 'extra_id',
];

// Noms lisibles des identifiants cites dans l'historique, une requete par table.
const lookupNames = async (table, ids, label) => {
  if (!ids?.length) return {};
  const { data, error } = await supabase?.from(table)?.select(`id, ${label}`)?.in('id', ids);
  if (error) return {};
  return (data || [])?.reduce((acc, row) => { acc[row?.id] = row?.[label]; return acc; }, {});
};

/**
 * Historique d'une directive (entity_change_log, ecrit par le trigger ;
 * l'app ne fait que lire). Plus recent d'abord. Renvoie
 * { rows: [...{ changedByName }], names: { professional_id, rfi_id, extra_id } }.
 */
export const listDirectiveHistory = async (directiveId) => {
  if (!directiveId) return { rows: [], names: {} };
  const { data, error } = await supabase
    ?.from('entity_change_log')
    ?.select('id, field, old_value, new_value, changed_at, changed_by, op')
    ?.eq('entity_type', 'project_directives')
    ?.eq('entity_id', directiveId)
    ?.order('changed_at', { ascending: false });
  if (error) {
    console.error('[directives] listDirectiveHistory:', error?.message);
    throw error;
  }
  const rows = data || [];
  const idsOf = (field) => [
    ...new Set(
      rows
        ?.filter((r) => r?.field === field)
        ?.flatMap((r) => [r?.old_value, r?.new_value])
        ?.filter(Boolean)
    ),
  ];
  const userIds = [...new Set(rows?.map((r) => r?.changed_by)?.filter(Boolean))];
  const [users, professional_id, rfi_id, extra_id] = await Promise.all([
    lookupNames('user_profiles', userIds, 'full_name'),
    lookupNames('professionals', idsOf('professional_id'), 'company_name'),
    lookupNames('project_rfis', idsOf('rfi_id'), 'rfi_number'),
    lookupNames('project_extras', idsOf('extra_id'), 'co_number'),
  ]);
  return {
    rows: rows?.map((r) => ({ ...r, changedByName: users?.[r?.changed_by] || null })),
    names: { professional_id, rfi_id, extra_id },
  };
};

// ─── Revenir a « A evaluer » selon le statut de l'extra lie (lot D9) ──────────

// Pieces d'un extra : meme dossier que la modale Documents de l'onglet Extras.
const EXTRA_STORAGE_PATH = (extraId) => `extra/${extraId}`;

// Nombre de lignes d'une table qui pointent vers l'extra (tete seule, sans donnees).
const countRefs = async (table, column, extraId, extra = (q) => q) => {
  const { count, error } = await extra(
    supabase?.from(table)?.select('id', { count: 'exact', head: true })?.eq(column, extraId)
  );
  if (error) throw error;
  return count || 0;
};

/**
 * Ce que « Revenir a A evaluer » ferait de l'extra lie, sans rien ecrire.
 *   mode 'draft'   brouillon, sans lien financier -> suppression, confirmation courte
 *   mode 'warn'    soumis ou rejete, sans lien financier -> suppression apres avertissement
 *   mode 'blocked' approuve, OU reference par une facture ST, une ligne de DP,
 *                  une depense ou un avenant envoye / signe -> aucune ecriture
 * Les references sont des cles ON DELETE SET NULL : la base accepterait la
 * suppression, mais ces documents perdraient leur extra sans le dire.
 */
export const getExtraRemovalCheck = async (extraId) => {
  if (!extraId) throw new Error('getExtraRemovalCheck: extra requis');
  const { data: extra, error } = await supabase
    ?.from('project_extras')
    ?.select('id, type, status, co_number, title, amount, cost_submitted, cost_accepted, profit')
    ?.eq('id', extraId)
    ?.single();
  if (error) throw error;

  const [invoices, invoiceCredits, invoiceLines, paymentItems, expenses, changeOrders] = await Promise.all([
    countRefs('sub_invoices', 'extra_id', extraId),
    countRefs('sub_invoices', 'credit_id', extraId),
    countRefs('sub_invoice_lines', 'extra_credit_id', extraId),
    countRefs('payment_items', 'linked_extra_id', extraId),
    countRefs('project_expenses', 'extra_id', extraId),
    countRefs('subcontract_change_orders', 'source_extra_id', extraId, (q) => q?.in('status', ['sent', 'signed'])),
  ]);
  const references = {
    invoices: invoices + invoiceCredits + invoiceLines,
    paymentRequests: paymentItems,
    expenses,
    changeOrders,
  };
  const referenced = Object.values(references)?.some((n) => n > 0);

  let mode = 'warn';
  if (extra?.status === 'approved' || referenced) mode = 'blocked';
  else if (extra?.status === 'draft') mode = 'draft';
  return { extra, mode, approved: extra?.status === 'approved', references };
};

/**
 * Revenir a « A evaluer » en supprimant l'extra lie (brouillon, soumis, rejete).
 *
 * Ordre impose par la base : project_directives.extra_id est ON DELETE SET NULL
 * et la contrainte outcome_link interdit extra_id NULL avec l'issue extra /
 * credit. Supprimer l'extra d'abord echouerait. Donc :
 *   0. nouvelle verification (l'extra a pu changer depuis l'ouverture) ;
 *   1. UN update de la directive : pending + extra_id NULL (reversible) ;
 *   2. suppression de l'extra (meme fonction que l'onglet Extras) ; en cas
 *      d'echec, la directive est remise dans son etat d'avant ;
 *   3. pieces Storage de l'extra retirees (au mieux).
 * Erreurs : EXTRA_REMOVAL_BLOCKED (avec .check), DIRECTIVE_REVERT_FAILED (rien
 * supprime), EXTRA_DELETE_FAILED (.restored = directive remise ou non).
 */
export const revertDirectiveDeletingExtra = async ({ directive }) => {
  const extraId = directive?.extra_id;
  if (!directive?.id || !extraId) throw new Error('revertDirectiveDeletingExtra: directive liee requise');

  const check = await getExtraRemovalCheck(extraId);
  if (check?.mode === 'blocked') {
    const err = new Error('EXTRA_REMOVAL_BLOCKED');
    err.check = check;
    throw err;
  }

  const previous = {
    outcome: directive?.outcome,
    extra_id: extraId,
    outcome_at: directive?.outcome_at ?? null,
    outcome_by: directive?.outcome_by ?? null,
  };

  // 1. La directive d'abord, en un seul update.
  const { error: revertError } = await supabase
    ?.from('project_directives')
    ?.update({ outcome: 'pending', extra_id: null, outcome_at: null, outcome_by: null })
    ?.eq('id', directive?.id);
  if (revertError) {
    console.error('[directives] revertDirectiveDeletingExtra (directive):', revertError?.message);
    const err = new Error('DIRECTIVE_REVERT_FAILED');
    err.cause = revertError;
    throw err;
  }

  // 2. L'extra.
  try {
    await projectExtrasService?.delete(extraId);
  } catch (deleteError) {
    console.error('[directives] revertDirectiveDeletingExtra (extra):', deleteError?.message);
    const { error: restoreError } = await supabase
      ?.from('project_directives')
      ?.update(previous)
      ?.eq('id', directive?.id);
    if (restoreError) console.error('[directives] revertDirectiveDeletingExtra (restauration):', restoreError?.message);
    const err = new Error('EXTRA_DELETE_FAILED');
    err.restored = !restoreError;
    err.cause = deleteError;
    throw err;
  }

  // 3. Ses pieces (au mieux : l'extra n'existe plus, un echec ne change rien d'autre).
  try {
    const { data: files } = await supabase?.storage?.from(BUCKET)?.list(EXTRA_STORAGE_PATH(extraId), { limit: 100 });
    const paths = (files || [])
      ?.filter((f) => f?.name && f?.name !== '.emptyFolderPlaceholder')
      ?.map((f) => `${EXTRA_STORAGE_PATH(extraId)}/${f?.name}`);
    if (paths?.length) await supabase?.storage?.from(BUCKET)?.remove(paths);
  } catch (storageError) {
    console.error('[directives] revertDirectiveDeletingExtra (Storage):', storageError?.message);
  }

  return { extra: check?.extra };
};

export default {
  listDirectives,
  listProjectProfessionals,
  listProjectRfis,
  createDirective,
  uploadDirectiveFile,
  countDirectiveFiles,
  markNoImpact,
  createOutcomeExtra,
  directiveStoragePath,
  updateDirective,
  setDirectiveOutcome,
  deleteDirective,
  listDirectiveHistory,
  listLinkableExtras,
  linkExistingExtra,
  getExtraRemovalCheck,
  revertDirectiveDeletingExtra,
};
